/**
 * AIが読み取れるドメイン — the hosts JMC has opened up for one clinic.
 *
 * Why it exists: a clinic may only have a page read if the URL's host matches
 * its own registered site URL. That is the right default and it refuses real
 * work - a booking application on its own domain, a campaign site, a second
 * brand - and the only workaround was to overwrite the clinic's site URL, which
 * is a different fact about the clinic and breaks the moment somebody corrects
 * it back.
 *
 * JMC WRITES, THE CLINIC READS. There is no clinic-side write path and there is
 * not meant to be one: migration 099 gives the table no tenant write arm at any
 * role, so a clinic cannot widen its own allowlist even through a mistake in a
 * route. The clinic sees the resulting list read-only on its own settings screen
 * so a refusal can be understood without a support ticket.
 *
 * It sits directly above 承認済み情報 because it governs the 「リンクから追加」
 * button inside that panel - the rule has to be readable before the control it
 * constrains.
 */
import { alertDialog } from './AppDialog.tsx';
import { del, post } from '../api.ts';
import { ConfirmButton } from './DangerButton.tsx';
import { Field } from './EditPanel.tsx';
import { ModalForm } from './Modal.tsx';
import { Card, Empty, Table } from './ui.tsx';
import { useState } from 'react';

export interface AllowedHost {
  id: string;
  host: string;
  include_subdomains: boolean;
  note?: string | null;
  created_at: string;
  created_by_name?: string | null;
}

/**
 * What the operator typed, as a hostname - or an explanation.
 *
 * Round-tripped through `URL` rather than pattern-matched, which eats a pasted
 * `https://`, a trailing path and a port, and punycodes 日本語ドメイン for free.
 * The server runs the same normalisation and the CHECK constraint sits behind
 * that; this copy exists only so the answer is immediate rather than a round
 * trip, and it is deliberately not the thing being relied on.
 */
function cleanHost(input: string, existing: string[]): string {
  const trimmed = input.trim().toLowerCase().replace(/^\*\./, '').replace(/\.$/, '');
  if (trimmed === '') throw new Error('ドメインを入力してください');
  let host: string;
  try {
    host = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`).hostname;
  } catch {
    throw new Error('ドメインの形式が正しくありません（例: example.co.jp）');
  }
  if (!host.includes('.')) throw new Error('ドメイン名を入力してください（例: example.co.jp）');
  if (existing.includes(host)) throw new Error(`${host} はすでに登録されています`);
  return host;
}

export function ClinicAllowedHosts({
  clinicId,
  hosts,
  onChanged,
}: {
  clinicId: string;
  hosts: AllowedHost[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState('');
  const [includeSubdomains, setIncludeSubdomains] = useState(false);

  /*
   * The same route for adding and for flipping the checkbox: it upserts on
   * (clinic_id, host), so re-posting a listed host changes the flag and keeps
   * created_at - the only record of when the clinic was trusted with it.
   */
  const save = (entry: { host: string; includeSubdomains: boolean }) =>
    post(`/admin/clinics/${clinicId}/allowed-hosts`, entry);

  return (
    <Card
      title="AIが読み取れるドメイン"
      caption="ここにあるドメインのページだけ、このクリニックの情報として読み取れます。クリニックのサイトURLのドメインは常に許可されます。"
      actions={
        <button
          className="btn btn--sm"
          type="button"
          onClick={() => {
            setHost('');
            setIncludeSubdomains(false);
            setOpen(true);
          }}
        >
          ＋ ドメインを許可
        </button>
      }
    >
      {hosts.length === 0 ? (
        <Empty>
          まだ登録されていません。クリニックのサイトURLのドメイン以外は読み取れません。
        </Empty>
      ) : (
        <Table
          head={
            <>
              <th>ドメイン</th>
              <th>サブドメイン</th>
              <th>追加</th>
              <th />
            </>
          }
        >
          {hosts.map((entry) => (
            <tr key={entry.id}>
              <td>{entry.host}</td>
              <td>
                {/*
                  A live control rather than a 編集 dialog: this is the only
                  editable thing on the row, and opening a form to tick one box
                  is more machinery than the change deserves.
                */}
                <label className="checkline">
                  <input
                    type="checkbox"
                    checked={entry.include_subdomains}
                    onChange={(event) => {
                      void save({
                        host: entry.host,
                        includeSubdomains: event.target.checked,
                      }).then(onChanged);
                    }}
                  />
                  <span>{entry.include_subdomains ? '含む' : 'このドメインのみ'}</span>
                </label>
              </td>
              <td className="muted small">
                {entry.created_by_name ?? '—'}
                <br />
                {new Date(entry.created_at).toLocaleDateString('ja-JP')}
              </td>
              <td className="rowactions">
                {/*
                  ConfirmButton, not DangerButton: removing a domain is
                  reversible in one dialog, and type-the-name is reserved for
                  things that are not. See the route - removal does NOT stop
                  pages already registered on this host from being re-read, and
                  the response says how many there are.
                */}
                <ConfirmButton
                  label="削除"
                  confirmLabel="本当に削除"
                  onConfirm={async () => {
                    const result = await del<{ registeredSources: number }>(
                      `/admin/clinics/${clinicId}/allowed-hosts/${entry.id}`,
                    );
                    if (result.registeredSources > 0) {
                      await alertDialog({
                        title: `${entry.host} を許可から外しました`,
                        message:
                          `このドメインで登録済みのページが ${result.registeredSources} 件あります。`
                          + '登録済みのページは引き続き再読み取りできます。'
                          + '完全に止めるには、承認済み情報の一覧から該当のURLを削除してください。',
                      });
                    }
                    onChanged();
                  }}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      <ModalForm
        open={open}
        onClose={() => setOpen(false)}
        title="ドメインを許可"
        caption="このクリニックのAIが読み取れるドメインを追加します。"
        submitLabel="許可する"
        onSubmit={async () => {
          await save({
            host: cleanHost(
              host,
              hosts.map((entry) => entry.host),
            ),
            includeSubdomains,
          });
          onChanged();
        }}
      >
        <Field label="ドメイン" htmlFor="allow-host" hint="https:// やパスは不要です。例: booking.example.co.jp">
          <input
            id="allow-host"
            className="input"
            value={host}
            placeholder="booking.example.co.jp"
            onChange={(event) => setHost(event.target.value)}
          />
        </Field>
        <Field
          label="サブドメイン"
          htmlFor="allow-subdomains"
          hint="オフのままなら、このドメインちょうどのページだけが読み取れます（www. も別のドメインとして扱われます）。"
        >
          <label className="checkline">
            <input
              id="allow-subdomains"
              type="checkbox"
              checked={includeSubdomains}
              onChange={(event) => setIncludeSubdomains(event.target.checked)}
            />
            <span>サブドメインも含む</span>
          </label>
        </Field>
      </ModalForm>
    </Card>
  );
}
